/**
 * WebSocket Service for Real-time Chat Notifications
 * 
 * This service manages a persistent WebSocket connection to receive
 * real-time notifications about new chat messages.
 */

import { isDemoMode } from '../demo/demoMode';

type MessageHandler = (message: any) => void;
type ConnectionStateHandler = (connected: boolean) => void;

interface WebSocketConfig {
  organizationId: string;
  accessToken: string;
  baseUrl?: string;
}

class ChatWebSocketService {
  private ws: WebSocket | null = null;
  private generation = 0; // Incremented on every connect/disconnect; guards stale event handlers
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 2000;
  private heartbeatIntervalMs = 25000; // 25s — under most proxy 30s idle timeouts
  private pongTimeoutMs = 10000;       // 10s to receive pong before declaring dead
  private pongTimeoutHandle: NodeJS.Timeout | null = null;
  private lastPongTime = 0;

  private config: WebSocketConfig | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private connectionStateHandlers: Set<ConnectionStateHandler> = new Set();
  private isIntentionallyClosed = false;

  /**
   * Connect to WebSocket server
   */
  connect(config: WebSocketConfig): void {
    // Demo mode: no real-time socket — stay silently "disconnected".
    if (isDemoMode()) {
      return;
    }

    // Don't reconnect if already connected or in the process of connecting
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.config = config;
    this.isIntentionallyClosed = false;

    // Bump generation so any in-flight handlers from the previous socket are ignored
    const myGeneration = ++this.generation;

    // Determine base URL (ws:// for local, wss:// for production)
    const baseUrl = config.baseUrl || this.getDefaultBaseUrl();
    const url = `${baseUrl}/chat/ws/${config.organizationId}?token=${config.accessToken}`;

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => { if (this.generation === myGeneration) this.handleOpen(); };
      this.ws.onmessage = (e) => { if (this.generation === myGeneration) this.handleMessage(e); };
      this.ws.onclose = (e) => { if (this.generation === myGeneration) this.handleClose(e); };
      this.ws.onerror = (e) => { if (this.generation === myGeneration) this.handleError(e); };
    } catch (error) {
      if (this.generation === myGeneration) this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    // Invalidate all pending handlers from the current socket before closing
    this.generation++;
    this.isIntentionallyClosed = true;
    this.stopHeartbeat();
    this.clearReconnectTimeout();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    this.notifyConnectionState(false);
  }

  /**
   * Subscribe to new message notifications
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to connection state changes
   */
  onConnectionStateChange(handler: ConnectionStateHandler): () => void {
    this.connectionStateHandlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.connectionStateHandlers.delete(handler);
    };
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Update access token (for token refresh scenarios)
   */
  updateToken(newToken: string): void {
    if (this.config) {
      this.config.accessToken = newToken;
      
      // Reconnect with new token if currently connected
      if (this.isConnected()) {
        this.disconnect();
        this.connect(this.config);
      }
    }
  }

  // ==================== Private Methods ====================

  private handleOpen(): void {
    this.reconnectAttempts = 0;
    this.lastPongTime = Date.now();
    this.startHeartbeat();
    this.notifyConnectionState(true);
  }

  private handleMessage(event: MessageEvent): void {
    const data = event.data;

    // Handle heartbeat response — record time and cancel the pong timeout
    if (data === 'pong') {
      this.lastPongTime = Date.now();
      this.clearPongTimeout();
      return;
    }

    // Handle new message notification from backend (backend sends "ping" as notification signal)
    if (data === 'ping') {
      // Notify all subscribers to fetch new messages
      const notification = { type: 'new_message' };
      this.messageHandlers.forEach(handler => {
        try {
          handler(notification);
        } catch (error) {
          // Silent error handling
        }
      });
      return;
    }

    // Parse JSON message (if backend sends full JSON in the future)
    try {
      const message = JSON.parse(data);
      
      // Notify all subscribers
      this.messageHandlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          // Silent error handling
        }
      });
    } catch (error) {
      // Silent error handling
    }
  }

  private handleClose(event: CloseEvent): void {
    this.stopHeartbeat();
    this.notifyConnectionState(false);

    if (!this.isIntentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.scheduleReconnect();
    }
  }

  private handleError(error: Event): void {
    // Force close so onclose fires and reconnect logic kicks in
    if (this.ws) {
      this.ws.close();
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('ping');

        // Start a pong timeout — if no pong arrives in time, the connection is a zombie
        this.clearPongTimeout();
        this.pongTimeoutHandle = setTimeout(() => {
          // No pong received — force close to trigger reconnect via onclose
          if (this.ws) {
            this.ws.close();
          }
        }, this.pongTimeoutMs);
      }
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    this.clearPongTimeout();
  }

  private clearPongTimeout(): void {
    if (this.pongTimeoutHandle) {
      clearTimeout(this.pongTimeoutHandle);
      this.pongTimeoutHandle = null;
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimeout();

    this.reconnectAttempts++;
    // Capped exponential backoff: 2s, 4s, 8s … max 30s
    const delay = Math.min(this.reconnectDelay * this.reconnectAttempts, 30000);

    this.reconnectTimeout = setTimeout(() => {
      if (this.config && !this.isIntentionallyClosed) {
        this.connect(this.config);
      }
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private notifyConnectionState(connected: boolean): void {
    this.connectionStateHandlers.forEach(handler => {
      try {
        handler(connected);
      } catch (error) {
        // Silent error handling
      }
    });
  }

  private getDefaultBaseUrl(): string {
    // Get backend URL from environment variable (fallback to production)
    const backendUrl = process.env.REACT_APP_API_BASE_URL || 'https://api2.mohasby.com';
    
    // Convert HTTP(S) to WS(S)
    if (backendUrl.startsWith('https://')) {
      return backendUrl.replace('https://', 'wss://');
    } else if (backendUrl.startsWith('http://')) {
      return backendUrl.replace('http://', 'ws://');
    }
    
    // Fallback (should never reach here)
    return 'wss://api.mohasby.com';
  }
}

// Singleton instance
export const chatWebSocketService = new ChatWebSocketService();
